export type SubmittalHierarchyItem = {
  id: number;
  parent?: number | null;
};

export type SubmittalDisplayItem<T extends SubmittalHierarchyItem> = T & {
  _depth: number;
};

type ItemComparator<T> = (a: T, b: T) => number;

const stableSort = <T>(items: T[], compare?: ItemComparator<T>): T[] => {
  if (!compare) return items;
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => compare(a.item, b.item) || a.index - b.index)
    .map(({ item }) => item);
};

export const getDescendantIds = <T extends SubmittalHierarchyItem>(
  items: T[],
  rootId: number,
): Set<number> => {
  const childrenByParent = new Map<number, number[]>();

  items.forEach((item) => {
    if (item.parent == null) return;
    const childIds = childrenByParent.get(item.parent) ?? [];
    childIds.push(item.id);
    childrenByParent.set(item.parent, childIds);
  });

  const descendants = new Set<number>();
  const pending = [...(childrenByParent.get(rootId) ?? [])];

  while (pending.length > 0) {
    const id = pending.pop() as number;
    if (descendants.has(id) || id === rootId) continue;
    descendants.add(id);
    pending.push(...(childrenByParent.get(id) ?? []));
  }

  return descendants;
};

export const includeSubmittalAncestors = <T extends SubmittalHierarchyItem>(
  allItems: T[],
  matchingItems: T[],
): T[] => {
  const itemById = new Map(allItems.map((item) => [item.id, item]));
  const includedIds = new Set(matchingItems.map((item) => item.id));

  matchingItems.forEach((item) => {
    let parentId = item.parent;
    const visited = new Set<number>([item.id]);

    while (parentId != null && !visited.has(parentId)) {
      visited.add(parentId);
      const parent = itemById.get(parentId);
      if (!parent) break;
      includedIds.add(parent.id);
      parentId = parent.parent;
    }
  });

  return allItems.filter((item) => includedIds.has(item.id));
};

export const flattenSubmittalHierarchy = <T extends SubmittalHierarchyItem>(
  items: T[],
  compare?: ItemComparator<T>,
): SubmittalDisplayItem<T>[] => {
  const itemById = new Map(items.map((item) => [item.id, item]));
  const childrenByParent = new Map<number, T[]>();

  items.forEach((item) => {
    if (item.parent == null || item.parent === item.id) return;
    const children = childrenByParent.get(item.parent) ?? [];
    children.push(item);
    childrenByParent.set(item.parent, children);
  });

  const roots = items.filter(
    (item) =>
      item.parent == null ||
      item.parent === item.id ||
      !itemById.has(item.parent),
  );
  const result: SubmittalDisplayItem<T>[] = [];
  const visited = new Set<number>();

  const appendBranch = (item: T, depth: number) => {
    if (visited.has(item.id)) return;
    visited.add(item.id);
    result.push({ ...item, _depth: depth });

    stableSort(childrenByParent.get(item.id) ?? [], compare).forEach((child) =>
      appendBranch(child, depth + 1),
    );
  };

  stableSort(roots, compare).forEach((root) => appendBranch(root, 0));

  // Keep malformed cyclic records visible instead of dropping them.
  stableSort(items, compare).forEach((item) => {
    if (!visited.has(item.id)) appendBranch(item, 0);
  });

  return result;
};

const compareValues = <T extends SubmittalHierarchyItem>(
  a: T,
  b: T,
  sortKey: keyof T,
  sortOrder: 'asc' | 'desc',
): number => {
  const aValue = a[sortKey] ?? '';
  const bValue = b[sortKey] ?? '';
  const aComparable =
    typeof aValue === 'string' && !isNaN(Number(aValue))
      ? Number(aValue)
      : aValue;
  const bComparable =
    typeof bValue === 'string' && !isNaN(Number(bValue))
      ? Number(bValue)
      : bValue;
  const direction = sortOrder === 'asc' ? 1 : -1;

  if (typeof aComparable === 'number' && typeof bComparable === 'number') {
    return aComparable === bComparable
      ? 0
      : aComparable < bComparable
        ? -direction
        : direction;
  }

  const aString = String(aComparable);
  const bString = String(bComparable);
  return aString === bString ? 0 : aString < bString ? -direction : direction;
};

export const sortSubmittalHierarchy = <T extends SubmittalHierarchyItem>(
  items: T[],
  sortKey: keyof T,
  sortOrder: 'asc' | 'desc',
): SubmittalDisplayItem<T>[] =>
  flattenSubmittalHierarchy(items, (a, b) =>
    compareValues(a, b, sortKey, sortOrder),
  );
